import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Loader2,
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  ListChecks,
  HelpCircle,
  FileText,
  Rocket,
  X,
  Plus,
  History,
} from 'lucide-react';
import { useToast } from './ui/toastContext';
import {
  abandonGuidedSession,
  advanceGuidedSession,
  confirmGuidedSession,
  createGuidedSession,
  getGuidedSession,
  listGuidedSessions,
} from '../api/guidedBuild';
import type {
  ClarifyGroup,
  ClarifyView,
  ComponentPlanItem,
  DiscoverView,
  Draft,
  GuidedSession,
  ImplementView,
  NotCreatingItem,
  PlanView,
  ReviewView,
} from '../api/guidedBuild';

// ============================================================
//  五阶段引导式建包（B1b，借鉴 create-cowork-plugin）
//  流程：需求描述 → 组件计划确认 → 分组澄清（可跳过）→ 草案预览 → 最终确认创建
//  中断后可从此列表恢复进行中会话
// ============================================================

const COMPONENT_TYPES: { value: ComponentPlanItem['type']; label: string }[] = [
  { value: 'persona', label: '人设' },
  { value: 'skill', label: '技能' },
  { value: 'knowledge', label: '知识库' },
  { value: 'tool', label: '工具' },
  { value: 'workflow', label: '工作流' },
];

const TYPE_LABEL: Record<string, string> = Object.fromEntries(COMPONENT_TYPES.map((t) => [t.value, t.label]));

function extractError(e: unknown): string {
  const err = e as { response?: { status?: number; data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (err.response?.status === 503) return '未配置 LLM，请先在「模型与通道」中添加 LLM 配置';
  if (typeof err.message === 'string') return err.message;
  return '未知错误';
}

export default function GuidedBuildWizard() {
  const [description, setDescription] = useState('');
  const [session, setSession] = useState<GuidedSession | null>(null);
  const [resumeList, setResumeList] = useState<GuidedSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 本地编辑态（计划表 / 草案 / 澄清回答），随会话切换重新初始化
  const [planComponents, setPlanComponents] = useState<ComponentPlanItem[]>([]);
  const [notCreating, setNotCreating] = useState<NotCreatingItem[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<string, string>>({});
  const [clarifySkipped, setClarifySkipped] = useState<string[]>([]);

  const navigate = useNavigate();
  const toast = useToast();

  // 挂载时拉取进行中会话，供断点恢复
  useEffect(() => {
    listGuidedSessions('active')
      .then(setResumeList)
      .catch(() => setResumeList([]));
  }, []);

  // 会话更新时同步初始化本地编辑态（避免异步 effect 的渲染竞态：按钮禁用/表格闪空）
  const applySession = (s: GuidedSession) => {
    setSession(s);
    const v = s.view;
    if (s.stage === 'discover' || s.stage === 'plan') {
      const pv = v as DiscoverView | PlanView;
      setPlanComponents((pv?.components || []).map((c) => ({ ...c })));
      setNotCreating((pv?.not_creating || []).map((n) => ({ ...n })));
    }
    if (s.stage === 'clarify') {
      const cv = v as ClarifyView;
      setClarifyAnswers(cv?.answers || {});
      setClarifySkipped(cv?.skipped_groups || []);
    }
    if (s.stage === 'implement' || s.stage === 'review') {
      const dv = v as ImplementView | ReviewView;
      if (dv?.draft) setDraft(JSON.parse(JSON.stringify(dv.draft)));
    }
  };

  const handleCreate = async () => {
    if (!description.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const s = await createGuidedSession(description.trim());
      applySession(s);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAdvance = async (payload: Parameters<typeof advanceGuidedSession>[1]) => {
    if (!session || loading) return;
    setLoading(true);
    setError(null);
    try {
      const s = await advanceGuidedSession(session.id, payload);
      if (s.status === 'abandoned') {
        setSession(null);
        setResumeList((prev) => prev.filter((x) => x.id !== s.id));
        toast.success('已放弃本次引导');
      } else {
        applySession(s);
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const s = await getGuidedSession(id);
      applySession(s);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAbandon = async () => {
    if (!session || loading) return;
    setLoading(true);
    setError(null);
    try {
      await abandonGuidedSession(session.id);
      setSession(null);
      setResumeList((prev) => prev.filter((x) => x.id !== session.id));
      toast.success('已放弃本次引导');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!session || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await confirmGuidedSession(session.id);
      setSession(result.session);
      const agentId = result.created?.agent_id;
      toast.success('Agent 创建成功');
      if (agentId) {
        navigate(`/agents/${agentId}/config-center`);
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const view = session?.view;

  const renderStepIndicator = () => {
    const labels: Record<string, string> = {
      discover: '发现与规划',
      plan: '确认计划',
      clarify: '澄清问答',
      implement: '草案预览',
      review: '最终确认',
    };
    const order = ['discover', 'plan', 'clarify', 'implement', 'review'];
    const idx = session ? order.indexOf(session.stage) : -1;
    return (
      <div className="wizard-steps" style={{ marginBottom: '16px' }}>
        {order.map((key, i) => (
          <div
            key={key}
            className={`wizard-step ${i === idx ? 'active' : ''} ${i < idx ? 'done' : ''}`}
          >
            <div className="wizard-step-number">{i < idx ? <Check size={14} /> : i + 1}</div>
            <div className="wizard-step-label">{labels[key]}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderError = () =>
    error && (
      <div
        className="glass-card"
        style={{
          marginBottom: '16px',
          padding: '12px',
          borderColor: 'var(--color-error)',
          color: 'var(--color-error)',
        }}
      >
        <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        {error}
      </div>
    );

  // ---------------- 步骤 0：需求描述 + 恢复入口 ----------------
  const renderInput = () => (
    <div className="animate-fade-in">
      <div className="glass-card" style={{ padding: '28px', textAlign: 'center' }}>
        <Sparkles size={36} style={{ color: 'var(--neon-purple, #a78bfa)', marginBottom: '12px' }} />
        <h3 style={{ marginBottom: '8px' }}>用一句话描述你想要的岗位 Agent</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '18px' }}>
          不需要填表。我们会分五步引导你：理解需求 → 规划组件 → 澄清细节 → 生成草案 → 确认创建。
          全程用大白话，不涉及任何技术配置。
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="例如：一个能独立处理售后退换货咨询的客服专家"
          rows={3}
          style={{ width: '100%', marginBottom: '16px' }}
          disabled={loading}
        />
        {renderError()}
        <button className="btn btn-primary" onClick={handleCreate} disabled={!description.trim() || loading}>
          {loading ? <Loader2 size={18} className="spin" /> : <Sparkles size={18} />}
          {loading ? '正在理解需求...' : '开始引导'}
        </button>
      </div>

      {resumeList.length > 0 && (
        <div className="glass-card" style={{ padding: '16px', marginTop: '16px' }}>
          <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <History size={18} style={{ color: 'var(--neon-blue, #60a5fa)' }} />
            <strong>继续之前未完成的引导（{resumeList.length}）</strong>
          </div>
          <div className="flex flex-col" style={{ gap: '8px' }}>
            {resumeList.map((s) => (
              <div
                key={s.id}
                className="flex"
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '8px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{s.agent_name || s.description.slice(0, 40)}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    阶段：{s.stage} · {s.description.slice(0, 50)}
                  </div>
                </div>
                <button className="btn btn-secondary" onClick={() => handleResume(s.id)} disabled={loading}>
                  <ChevronRight size={16} /> 继续
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ---------------- 步骤 1/2：发现小结 + 组件计划表 ----------------
  const updatePlanComponent = (idx: number, patch: Partial<ComponentPlanItem>) => {
    setPlanComponents((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const renderComponentTable = (components: ComponentPlanItem[], editable: boolean) => (
    <div className="flex flex-col" style={{ gap: '8px' }}>
      {components.map((c, idx) => (
        <div
          key={idx}
          className="flex"
          style={{ gap: '8px', alignItems: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '8px 10px' }}
        >
          {editable ? (
            <select
              value={c.type}
              onChange={(e) => updatePlanComponent(idx, { type: e.target.value as ComponentPlanItem['type'] })}
              style={{ width: 110 }}
            >
              {COMPONENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          ) : (
            <span className="feature-tag" style={{ minWidth: 60, textAlign: 'center' }}>
              {TYPE_LABEL[c.type] || c.type}
            </span>
          )}
          {editable ? (
            <input
              value={c.name}
              placeholder="组件名称"
              onChange={(e) => updatePlanComponent(idx, { name: e.target.value })}
              style={{ flex: 1 }}
            />
          ) : (
            <strong style={{ flex: 1 }}>{c.name}</strong>
          )}
          {editable ? (
            <input
              value={c.purpose}
              placeholder="用途（做什么、解决什么问题）"
              onChange={(e) => updatePlanComponent(idx, { purpose: e.target.value })}
              style={{ flex: 2 }}
            />
          ) : (
            <span style={{ flex: 2, color: 'var(--text-secondary)', fontSize: '13px' }}>{c.purpose}</span>
          )}
          {editable && (
            <button
              className="icon-btn danger"
              onClick={() => setPlanComponents((prev) => prev.filter((_, i) => i !== idx))}
            >
              <X size={14} />
            </button>
          )}
        </div>
      ))}
      {editable && (
        <button
          className="btn btn-secondary"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setPlanComponents((prev) => [...prev, { type: 'skill', name: '', purpose: '' }])}
        >
          <Plus size={16} /> 添加组件
        </button>
      )}
      {components.length === 0 && <div className="muted">尚未规划组件</div>}
    </div>
  );

  const renderDiscover = () => {
    const v = view as DiscoverView;
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '12px' }}>需求理解与组件规划</h3>
        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <ListChecks size={18} style={{ color: 'var(--neon-blue, #60a5fa)' }} />
            <strong>需求理解小结</strong>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{v.summary}</p>
          {v.target_role && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              定位：<strong>{v.target_role}</strong>
            </div>
          )}
        </div>

        {v.recommendation && (
          <div
            className="glass-card"
            style={{
              padding: '14px',
              marginBottom: '14px',
              borderColor: 'var(--neon-yellow, #facc15)',
            }}
          >
            <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Lightbulb size={18} style={{ color: 'var(--neon-yellow, #facc15)' }} />
              <strong>给你的具体建议（请确认或调整后继续）</strong>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              {v.recommendation.recommendation}
            </p>
          </div>
        )}

        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>计划创建的组件（可调整）</strong>
          {renderComponentTable(planComponents, true)}
        </div>

        {notCreating.length > 0 && (
          <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
            <strong style={{ display: 'block', marginBottom: '10px' }}>这次先不创建的部分及原因</strong>
            <div className="flex flex-col" style={{ gap: '6px' }}>
              {notCreating.map((n, idx) => (
                <div key={idx} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <span className="feature-tag">{TYPE_LABEL[n.type] || n.type}</span>{' '}
                  <strong>{n.name}</strong> —— {n.reason || '暂无说明'}
                </div>
              ))}
            </div>
          </div>
        )}

        {renderError()}
        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button className="btn btn-secondary" onClick={handleAbandon} disabled={loading}>
            放弃本次引导
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleAdvance({ note: '你看着办，帮我规划' })}
            disabled={loading}
          >
            {loading ? <Loader2 size={18} className="spin" /> : <Lightbulb size={18} />} 你看着办
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              handleAdvance({
                confirm_plan: true,
                plan: { components: planComponents, not_creating: notCreating },
              })
            }
            disabled={loading || planComponents.length === 0}
          >
            {loading ? <Loader2 size={18} className="spin" /> : <Check size={18} />} 确认计划并继续
          </button>
        </div>
      </div>
    );
  };

  const renderPlan = () => {
    const v = view as PlanView;
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '12px' }}>组件计划已确认</h3>
        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>计划创建的组件</strong>
          {renderComponentTable(v.components, false)}
        </div>
        {v.not_creating.length > 0 && (
          <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
            <strong style={{ display: 'block', marginBottom: '10px' }}>这次先不创建的部分及原因</strong>
            <div className="flex flex-col" style={{ gap: '6px' }}>
              {v.not_creating.map((n, idx) => (
                <div key={idx} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <span className="feature-tag">{TYPE_LABEL[n.type] || n.type}</span>{' '}
                  <strong>{n.name}</strong> —— {n.reason || '暂无说明'}
                </div>
              ))}
            </div>
          </div>
        )}
        {renderError()}
        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={() => setSession(null)} disabled={loading}>
            <ChevronLeft size={18} /> 返回修改描述
          </button>
          <button className="btn btn-primary" onClick={() => handleAdvance({})} disabled={loading}>
            {loading ? <Loader2 size={18} className="spin" /> : <HelpCircle size={18} />} 继续 → 澄清问答
          </button>
        </div>
      </div>
    );
  };

  // ---------------- 步骤 3：分组澄清 ----------------
  const renderClarify = () => {
    const v = view as ClarifyView;
    const toggleGroup = (title: string) => {
      setClarifySkipped((prev) => (prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]));
    };

    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '12px' }}>几个小问题，帮我把细节定准</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '14px' }}>
          每个问题组都可以整组跳过——不知道答案也没关系，我们会用合理默认值继续。
        </p>
        <div className="flex flex-col" style={{ gap: '12px' }}>
          {v.question_groups.map((g: ClarifyGroup) => {
            const groupSkipped = clarifySkipped.includes(g.title);
            return (
              <div key={g.title} className="glass-card" style={{ padding: '16px' }}>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong>{g.title}</strong>
                  <label className="flex" style={{ gap: '6px', alignItems: 'center', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={groupSkipped}
                      onChange={() => toggleGroup(g.title)}
                    />
                    跳过这组
                  </label>
                </div>
                {groupSkipped ? (
                  <div className="muted">已跳过，使用默认值继续</div>
                ) : (
                  <div className="flex flex-col" style={{ gap: '10px' }}>
                    {g.questions.map((q) => (
                      <div key={q.id}>
                        <label className="form-label">{q.question}</label>
                        <input
                          value={clarifyAnswers[q.id] || ''}
                          placeholder={q.options?.length ? `可选：${q.options.join(' / ')}` : '输入你的回答，或留空'}
                          onChange={(e) => setClarifyAnswers({ ...clarifyAnswers, [q.id]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {renderError()}
        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button className="btn btn-secondary" onClick={() => handleAdvance({})} disabled={loading}>
            {loading ? <Loader2 size={18} className="spin" /> : <Check size={18} />} 全部跳过
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleAdvance({ answers: clarifyAnswers, skipped_groups: clarifySkipped })}
            disabled={loading}
          >
            {loading ? <Loader2 size={18} className="spin" /> : <Sparkles size={18} />} 提交回答并生成草案
          </button>
        </div>
      </div>
    );
  };

  // ---------------- 步骤 4：草案预览（可编辑） ----------------
  const updateSkill = (idx: number, patch: Partial<Draft['skills'][number]>) => {
    if (!draft) return;
    const skills = draft.skills.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setDraft({ ...draft, skills });
  };

  const renderImplement = () => {
    if (!draft) return null;
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '12px' }}>生成的 Agent 草案</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '14px' }}>
          以下是根据你的需求和回答推导出的 Agent，可以直接修改，然后生成最终确认页。
        </p>
        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>基础信息</strong>
          <div className="flex flex-col" style={{ gap: '12px' }}>
            <div className="grid grid-2" style={{ gap: '12px' }}>
              <div>
                <label className="form-label">名称</label>
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={64} />
              </div>
              <div>
                <label className="form-label">领域</label>
                <input
                  value={draft.domain || ''}
                  onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                  placeholder="例如：客服、技术支持"
                />
              </div>
            </div>
            <div>
              <label className="form-label">描述</label>
              <textarea
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-2" style={{ gap: '12px' }}>
              <div>
                <label className="form-label">人设角色</label>
                <input
                  value={draft.persona?.identity?.role || ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      persona: {
                        ...draft.persona,
                        identity: { ...(draft.persona?.identity || {}), role: e.target.value },
                      },
                    })
                  }
                />
              </div>
              <div>
                <label className="form-label">语气</label>
                <input
                  value={draft.persona?.communication?.tone || ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      persona: {
                        ...draft.persona,
                        communication: { ...(draft.persona?.communication || {}), tone: e.target.value },
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>初始技能（{draft.skills.length} 个）</strong>
          <div className="flex flex-col" style={{ gap: '12px' }}>
            {draft.skills.map((skill, idx) => (
              <div key={idx} className="glass-card" style={{ padding: '12px' }}>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    value={skill.display_name}
                    onChange={(e) => updateSkill(idx, { display_name: e.target.value })}
                    style={{ fontWeight: 600 }}
                  />
                  <button
                    className="icon-btn danger"
                    onClick={() => setDraft({ ...draft, skills: draft.skills.filter((_, i) => i !== idx) })}
                    disabled={draft.skills.length <= 1}
                    title={draft.skills.length <= 1 ? '至少保留一个技能' : '删除技能'}
                  >
                    <X size={16} />
                  </button>
                </div>
                <textarea
                  value={skill.instructions || ''}
                  onChange={(e) => updateSkill(idx, { instructions: e.target.value })}
                  placeholder="技能指令（可复用的自然语言指令）"
                  rows={2}
                />
              </div>
            ))}
            <button
              className="btn btn-secondary"
              onClick={() =>
                setDraft({ ...draft, skills: [...draft.skills, { display_name: '', instructions: '', category: 'general' }] })
              }
              disabled={draft.skills.length >= 6}
            >
              <Plus size={16} /> 添加技能
            </button>
          </div>
        </div>

        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Lightbulb size={18} style={{ color: 'var(--neon-yellow, #facc15)' }} />
            <strong>AI 做出的假设（{draft.assumptions.length} 条）</strong>
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>
            {draft.assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>

        {renderError()}
        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={() => handleAdvance({})} disabled={loading}>
            返回上一步
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              handleAdvance({
                draft_overrides: {
                  name: draft.name,
                  description: draft.description,
                  domain: draft.domain,
                  persona: draft.persona,
                  skills: draft.skills,
                },
              })
            }
            disabled={loading || !draft.name.trim() || draft.skills.length === 0}
          >
            {loading ? <Loader2 size={18} className="spin" /> : <FileText size={18} />} 生成最终确认
          </button>
        </div>
      </div>
    );
  };

  // ---------------- 步骤 5：最终确认 ----------------
  const renderReview = () => {
    const v = view as ReviewView;
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '12px' }}>最终确认</h3>
        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>将要创建的 Agent</strong>
          <div className="review-row"><span>名称</span><strong>{v.draft.name}</strong></div>
          <div className="review-row"><span>领域</span><strong>{v.draft.domain || '未设置'}</strong></div>
          <div className="review-row"><span>描述</span><strong>{v.draft.description}</strong></div>
          <div className="review-row">
            <span>人设</span>
            <strong>{v.draft.persona?.identity?.role || '未设置'}</strong>
          </div>
          <div className="review-row"><span>初始技能</span><strong>{v.draft.skills.length} 个</strong></div>
        </div>
        <div className="glass-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>组件计划回顾</strong>
          <div className="flex flex-col" style={{ gap: '6px' }}>
            {v.plan.components.map((c, idx) => (
              <div key={idx} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span className="feature-tag">{TYPE_LABEL[c.type] || c.type}</span>{' '}
                <strong>{c.name}</strong> —— {c.purpose}
              </div>
            ))}
            {v.plan.not_creating.length > 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                本次不创建：{v.plan.not_creating.map((n) => `${n.name}（${n.reason || '暂无说明'}）`).join('、')}
              </div>
            )}
          </div>
        </div>
        {v.summary && (
          <div className="glass-card" style={{ padding: '14px', marginBottom: '14px', fontSize: '13px' }}>
            <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Lightbulb size={16} style={{ color: 'var(--neon-yellow, #facc15)' }} />
              <strong>需求回顾</strong>
            </div>
            <span style={{ color: 'var(--text-secondary)' }}>{v.summary}</span>
          </div>
        )}
        {renderError()}
        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={handleAbandon} disabled={loading}>
            放弃本次引导
          </button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={loading}>
            {loading ? <Loader2 size={18} className="spin" /> : <Rocket size={18} />} 确认创建 Agent
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="animate-fade-in" style={{ textAlign: 'center' }}>
      <div className="glass-card neon-border-green" style={{ padding: '48px' }}>
        <Check size={56} style={{ color: 'var(--neon-green)', marginBottom: '14px' }} />
        <h2 style={{ color: 'var(--neon-green)' }}>Agent 创建完成</h2>
        <p style={{ color: 'var(--text-secondary)' }}>
          {session?.agent_name || '你的岗位 Agent 已就绪'}，即将前往配置中心
        </p>
        <button className="btn btn-primary" onClick={() => session?.agent_id && navigate(`/agents/${session.agent_id}/config-center`)}>
          前往配置中心
        </button>
      </div>
    </div>
  );

  // ---------------- 主渲染 ----------------
  if (!session) {
    return (
      <div className="animate-fade-in">
        {renderInput()}
      </div>
    );
  }

  if (session.status === 'completed' && session.agent_id) {
    return renderCompleted();
  }

  return (
    <div className="animate-fade-in">
      {renderStepIndicator()}
      {session.stage === 'discover' && renderDiscover()}
      {session.stage === 'plan' && renderPlan()}
      {session.stage === 'clarify' && renderClarify()}
      {session.stage === 'implement' && renderImplement()}
      {session.stage === 'review' && renderReview()}
    </div>
  );
}
